/**
 * ParentPortal - Parent/Guardian's dedicated portal
 * Dashboard, Children overview, Attendance, Grades, Timetable, Report Cards
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, Users, Clock, Trophy, CalendarRange, FileText,
  LogOut, GraduationCap, Loader2, ChevronLeft, ChevronRight, User, CheckCircle, XCircle,
  Bell, AlertTriangle, DollarSign, Smartphone, Phone, Lock
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthStore } from '@/lib/store';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { PortalNotificationBell } from '@/components/school/PortalNotificationBell';
import { SemesterPicker, useStudentSemesters } from '@/components/school/StudentSemester';
import FeeGateScreen from '@/components/school/FeeGateScreen';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import TranslatePageButton from '@/components/TranslatePageButton';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { formatNumber } from '@/lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Handle View-As preview token from URL
const initPreviewToken = () => {
  const params = new URLSearchParams(window.location.search);
  const pt = params.get('preview_token');
  if (pt) {
    sessionStorage.setItem('school_preview_token', pt);
    window.history.replaceState({}, '', window.location.pathname);
  }
};
initPreviewToken();
const getEffectiveToken = () => {
  try {
    const previewToken = sessionStorage.getItem('school_preview_token');
    if (previewToken) return previewToken;
  } catch (_e) { /* sessionStorage unavailable */ }
  return getToken();
};
const portalApi = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school/portal${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getEffectiveToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const GRADE_COLORS = {
  'A+': 'text-green-700 bg-green-50', 'A': 'text-green-600 bg-green-50',
  'B+': 'text-blue-700 bg-blue-50', 'B': 'text-blue-600 bg-blue-50',
  'C': 'text-amber-700 bg-amber-50', 'D': 'text-orange-700 bg-orange-50',
  'F': 'text-red-700 bg-red-50',
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Menu uses i18n nav keys so labels translate at render time.
const MENU = [
  { id: 'dashboard', navKey: 'nav.dashboard', icon: LayoutDashboard },
  { id: 'children', navKey: 'nav.children', icon: Users },
  { id: 'attendance', navKey: 'nav.attendance', icon: Clock },
  { id: 'grades', navKey: 'nav.grades', icon: Trophy },
  { id: 'timetable', navKey: 'nav.timetable', icon: CalendarRange },
  { id: 'report', navKey: 'nav.report_card', icon: FileText },
  { id: 'announcements', navKey: 'nav.announcements', icon: Bell },
  { id: 'fees', navKey: 'nav.fees', icon: DollarSign },
  { id: 'risk-alerts', navKey: 'nav.risk_alerts', icon: AlertTriangle },
  { id: 'notif-prefs', navKey: 'nav.notif_prefs', icon: Smartphone },
];

// ============== CHILD SELECTOR ==============
const ChildSelector = ({ children, selected, onSelect }) => {
  if (!children?.length) return null;
  return (
    <div className="flex gap-2 flex-wrap" data-testid="child-selector">
      {children.map(child => (
        <button key={child.id} onClick={() => onSelect(child.id)}
          className={cn("flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border",
            selected === child.id
              ? "bg-amber-50 border-amber-300 text-amber-800"
              : "bg-white border-slate-200 text-slate-600 hover:border-amber-200")}
          data-testid={`select-child-${child.id}`}>
          <User className="h-4 w-4" />
          <span>{child.first_name} {child.last_name}</span>
          {child.class_name && <Badge variant="outline" className="text-xs">{child.class_name}</Badge>}
        </button>
      ))}
    </div>
  );
};

// ============== PARENT DASHBOARD ==============
const ParentDashboard = ({ tenantId }) => {
  const { data: profile } = useQuery({
    queryKey: ['parent-profile'],
    queryFn: () => portalApi('/me'),
  });
  const { data: children = [], isLoading } = useQuery({
    queryKey: ['parent-children', tenantId],
    queryFn: () => portalApi('/parent/children'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      <h2 className="text-xl font-bold text-slate-900">Welcome, {profile?.name || 'Parent'}</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4 text-center">
          <Users className="h-5 w-5 mx-auto mb-1 text-amber-500" />
          <p className="text-2xl font-bold">{children.length}</p>
          <p className="text-xs text-slate-500">{children.length === 1 ? 'Child' : 'Children'}</p>
        </CardContent></Card>
        {children.map(child => (
          <Card key={child.id}><CardContent className="p-4 text-center">
            <GraduationCap className="h-5 w-5 mx-auto mb-1 text-indigo-500" />
            <p className="text-lg font-bold">{child.attendance_rate}%</p>
            <p className="text-xs text-slate-500">{child.first_name}'s Attendance</p>
          </CardContent></Card>
        ))}
      </div>

      <h3 className="text-base font-semibold text-slate-800">Children Overview</h3>
      {children.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No children linked to your account</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {children.map(child => (
            <Card key={child.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{child.first_name} {child.last_name}</h4>
                    <p className="text-xs text-slate-500">
                      {child.class_name || 'No class'} | {child.admission_number || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 rounded-lg p-2">
                    <p className="text-sm font-bold text-green-700">{child.attendance_rate}%</p>
                    <p className="text-[10px] text-green-600">Attendance</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2">
                    <p className="text-sm font-bold text-blue-700">{child.total_days}</p>
                    <p className="text-[10px] text-blue-600">School Days</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-sm font-bold text-slate-700 capitalize">{child.status || 'active'}</p>
                    <p className="text-[10px] text-slate-600">Status</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== CHILDREN LIST ==============
const ParentChildren = ({ tenantId }) => {
  const { data: children = [], isLoading } = useQuery({
    queryKey: ['parent-children', tenantId],
    queryFn: () => portalApi('/parent/children'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-4" data-testid="parent-children">
      <h2 className="text-lg font-semibold">My Children</h2>
      {children.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No children linked to your account. Contact the school administrator.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {children.map(child => (
            <Card key={child.id}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{child.first_name} {child.last_name}</h3>
                      <p className="text-sm text-slate-500">
                        Admission: {child.admission_number || 'N/A'} | Class: {child.class_name || 'Not assigned'}
                      </p>
                      <p className="text-xs text-slate-400 capitalize">Relationship: {child.relationship}</p>
                    </div>
                  </div>
                  <Badge className={child.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                    {child.status || 'active'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                  <div className="text-center p-2 bg-green-50 rounded-lg">
                    <CheckCircle className="h-4 w-4 mx-auto mb-1 text-green-600" />
                    <p className="text-sm font-bold text-green-700">{child.attendance_rate}%</p>
                    <p className="text-xs text-green-600">Attendance</p>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <Clock className="h-4 w-4 mx-auto mb-1 text-blue-600" />
                    <p className="text-sm font-bold text-blue-700">{child.total_days}</p>
                    <p className="text-xs text-blue-600">Days Tracked</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                    <GraduationCap className="h-4 w-4 mx-auto mb-1 text-slate-600" />
                    <p className="text-sm font-bold text-slate-700">{child.gender || '-'}</p>
                    <p className="text-xs text-slate-600">Gender</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== ATTENDANCE ==============
const ParentAttendance = ({ tenantId }) => {
  const { data: children = [] } = useQuery({
    queryKey: ['parent-children', tenantId],
    queryFn: () => portalApi('/parent/children'),
  });
  const [selectedChild, setSelectedChild] = useState(null);

  React.useEffect(() => {
    if (children.length > 0 && !selectedChild) setSelectedChild(children[0].id);
  }, [children, selectedChild]);

  const { data: attendance, isLoading } = useQuery({
    queryKey: ['parent-child-att', tenantId, selectedChild],
    queryFn: () => portalApi(`/parent/children/${selectedChild}/attendance`),
    enabled: !!selectedChild,
  });

  return (
    <div className="space-y-4" data-testid="parent-attendance">
      <h2 className="text-lg font-semibold">Attendance</h2>
      <ChildSelector children={children} selected={selectedChild} onSelect={setSelectedChild} />

      {selectedChild && !isLoading && attendance && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-green-600">{attendance.summary.present}</p>
              <p className="text-xs text-slate-500">Present</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-red-600">{attendance.summary.absent}</p>
              <p className="text-xs text-slate-500">Absent</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-amber-600">{attendance.summary.late}</p>
              <p className="text-xs text-slate-500">Late</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-indigo-600">{attendance.summary.attendance_rate}%</p>
              <p className="text-xs text-slate-500">Rate</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Attendance History</CardTitle></CardHeader>
            <CardContent>
              {attendance.records.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No records yet</p>
              ) : (
                <div className="space-y-1">
                  {attendance.records.map((r, i) => (
                    <div key={`${r.date}-${r.status}-${i}`} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
                      <span className="text-slate-600">{r.date}</span>
                      <Badge className={
                        r.status === 'present' ? 'bg-green-100 text-green-700' :
                        r.status === 'absent' ? 'bg-red-100 text-red-700' :
                        r.status === 'late' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                      }>{r.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
      {selectedChild && isLoading && <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
    </div>
  );
};

// ============== GRADES ==============
const ParentGrades = ({ tenantId }) => {
  const { data: children = [] } = useQuery({
    queryKey: ['parent-children', tenantId],
    queryFn: () => portalApi('/parent/children'),
  });
  const [selectedChild, setSelectedChild] = useState(null);

  React.useEffect(() => {
    if (children.length > 0 && !selectedChild) setSelectedChild(children[0].id);
  }, [children, selectedChild]);

  const { flat, termId, setTermId } = useStudentSemesters(portalApi, '/parent/semesters');
  const { data: grades, isLoading } = useQuery({
    queryKey: ['parent-child-grades', tenantId, selectedChild, termId],
    queryFn: () => portalApi(
      `/parent/children/${selectedChild}/grades${termId ? `?term_id=${termId}` : ''}`),
    enabled: !!selectedChild,
  });

  return (
    <div className="space-y-4" data-testid="parent-grades">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">Grades</h2>
        <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                        testId="parent-grades-semester-picker" />
      </div>
      <ChildSelector children={children} selected={selectedChild} onSelect={setSelectedChild} />

      {selectedChild && !isLoading && grades && (
        <>
          {!grades.results?.length ? (
            <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No exam results yet</CardContent></Card>
          ) : grades.results.map((exam, i) => (
            <Card key={exam.exam_id || `${exam.exam_name}-${i}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{exam.exam_name}</CardTitle>
                  <Badge variant="outline" className="text-sm">{exam.percentage}%</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead className="text-center">Marks</TableHead>
                    <TableHead className="text-center">%</TableHead>
                    <TableHead className="text-center">Grade</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {exam.subjects.map((s, j) => (
                      <TableRow key={s.subject_id || s.subject_name || j}>
                        <TableCell className="font-medium">{s.subject_name}</TableCell>
                        <TableCell className="text-center">{s.marks}/{s.max_marks}</TableCell>
                        <TableCell className="text-center">{s.percentage}%</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${GRADE_COLORS[s.grade] || ''}`}>{s.grade}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </>
      )}
      {selectedChild && isLoading && <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
    </div>
  );
};

// ============== TIMETABLE ==============
const ParentTimetable = ({ tenantId }) => {
  const { data: children = [] } = useQuery({
    queryKey: ['parent-children', tenantId],
    queryFn: () => portalApi('/parent/children'),
  });
  const [selectedChild, setSelectedChild] = useState(null);

  React.useEffect(() => {
    if (children.length > 0 && !selectedChild) setSelectedChild(children[0].id);
  }, [children, selectedChild]);

  const { flat, termId, setTermId } = useStudentSemesters(portalApi, '/parent/semesters');
  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['parent-child-timetable', tenantId, selectedChild, termId],
    queryFn: () => portalApi(
      `/parent/children/${selectedChild}/timetable${termId ? `?term_id=${termId}` : ''}`),
    enabled: !!selectedChild,
  });

  const grouped = {};
  DAYS.forEach(d => { grouped[d] = []; });
  slots.forEach(s => {
    const day = s.day_of_week || 'Monday';
    if (grouped[day]) grouped[day].push(s);
  });
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));

  return (
    <div className="space-y-4" data-testid="parent-timetable">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">Timetable</h2>
        <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                        testId="parent-timetable-semester-picker" />
      </div>
      <ChildSelector children={children} selected={selectedChild} onSelect={setSelectedChild} />

      {selectedChild && !isLoading && (
        slots.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No timetable available</CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {DAYS.map(day => (
              <Card key={day}>
                <CardHeader className="py-3 px-4"><CardTitle className="text-sm font-semibold">{day}</CardTitle></CardHeader>
                <CardContent className="px-4 pb-3">
                  {grouped[day].length === 0 ? (
                    <p className="text-xs text-slate-400">No classes</p>
                  ) : (
                    <div className="space-y-1.5">
                      {grouped[day].map((s, i) => (
                        <div key={s.id || `${s.subject_name}-${s.start_time}-${i}`} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="font-medium text-sm">{s.subject_name}</p>
                            <p className="text-xs text-slate-500">{s.teacher_name}</p>
                          </div>
                          <Badge variant="outline" className="text-xs font-mono">{s.start_time} - {s.end_time}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
      {selectedChild && isLoading && <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
    </div>
  );
};

// ============== REPORT CARD ==============
const ParentReportCard = ({ tenantId }) => {
  const { data: children = [] } = useQuery({
    queryKey: ['parent-children', tenantId],
    queryFn: () => portalApi('/parent/children'),
  });
  const [selectedChild, setSelectedChild] = useState(null);

  React.useEffect(() => {
    if (children.length > 0 && !selectedChild) setSelectedChild(children[0].id);
  }, [children, selectedChild]);

  const { data: report, isLoading } = useQuery({
    queryKey: ['parent-child-report', tenantId, selectedChild],
    queryFn: () => portalApi(`/parent/children/${selectedChild}/report-card`),
    enabled: !!selectedChild,
  });

  return (
    <div className="space-y-4" data-testid="parent-report">
      <h2 className="text-lg font-semibold">Report Card</h2>
      <ChildSelector children={children} selected={selectedChild} onSelect={setSelectedChild} />

      {selectedChild && !isLoading && report && report.gated && (
        <Card className="border-amber-300 bg-amber-50" data-testid="parent-report-card-gated">
          <CardContent className="p-6 text-center space-y-3">
            <Lock className="h-8 w-8 mx-auto text-amber-600" />
            <h3 className="text-lg font-semibold">{report.exam_name || 'Report card'}</h3>
            <p className="text-sm text-slate-700">{report.message}</p>
            {report.reason === 'fees_outstanding' && report.outstanding_amount != null && (
              <div className="inline-flex items-center gap-2 bg-white border border-amber-300 rounded px-4 py-2">
                <DollarSign className="h-4 w-4 text-amber-600" />
                <span className="font-bold">{report.currency || '$'}{Number(report.outstanding_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                <span className="text-xs text-slate-500">outstanding</span>
              </div>
            )}
            {report.reason === 'not_yet_released' && report.release_date && (
              <p className="text-xs text-slate-500">Releases {new Date(report.release_date).toLocaleString()}</p>
            )}
          </CardContent>
        </Card>
      )}

      {selectedChild && !isLoading && report && !report.gated && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">{report.student?.name}</CardTitle>
                <p className="text-xs text-slate-500">Admission: {report.student?.admission_number}</p>
              </div>
              {report.exam && (
                <div className="text-right space-y-1">
                  <p className="font-semibold text-sm">{report.exam.name}</p>
                  {report.pass_fail?.label && (
                    <Badge className={
                      report.pass_fail.classification === 'distinction' ? 'bg-emerald-100 text-emerald-800' :
                      report.pass_fail.classification === 'merit' ? 'bg-blue-100 text-blue-800' :
                      report.pass_fail.classification === 'credit' ? 'bg-indigo-100 text-indigo-800' :
                      report.pass_fail.is_pass ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                    }>{report.pass_fail.label}</Badge>
                  )}
                  {(report.summary?.percentage != null) && (
                    <Badge variant="outline" className="text-sm ml-2">{report.summary.percentage}%</Badge>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {report.status === 'awaiting_publication' ? (
              <p className="text-sm text-amber-700 text-center py-4">{report.message || 'Results not published yet.'}</p>
            ) : !report.subjects?.length ? (
              <p className="text-sm text-slate-400 text-center py-4">{report.message || 'No results available'}</p>
            ) : (
              <>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead className="text-center">Marks</TableHead>
                    <TableHead className="text-center">%</TableHead>
                    <TableHead className="text-center">Grade</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.subjects.map((s, j) => (
                      <TableRow key={j}>
                        <TableCell className="font-medium">{s.subject_name}</TableCell>
                        <TableCell className="text-center">{s.marks}/{s.max_marks}</TableCell>
                        <TableCell className="text-center">{s.percentage}%</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${GRADE_COLORS[s.grade] || ''}`}>{s.grade}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-4 p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-sm text-slate-600">Total: <span className="font-bold">{report.summary?.total_marks}/{report.summary?.total_max}</span></p>
                  <p className="text-lg font-bold text-indigo-600">{report.summary?.percentage}%</p>
                  {report.position && <p className="text-xs text-slate-500 mt-1">Position {report.position}/{report.class_size}</p>}
                </div>
                {report.attendance?.total_days > 0 && (
                  <p className="mt-3 text-xs text-slate-500">
                    Attendance: {report.attendance.present}/{report.attendance.total_days} ({report.attendance.rate}%)
                  </p>
                )}
                {(report.comments?.class_teacher || report.comments?.head_teacher) && (
                  <div className="mt-3 text-xs space-y-1 border-t pt-2">
                    {report.comments.class_teacher && <p><span className="font-semibold">Class teacher:</span> {report.comments.class_teacher}</p>}
                    {report.comments.head_teacher && <p><span className="font-semibold">Head teacher:</span> {report.comments.head_teacher}</p>}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
      {selectedChild && isLoading && <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
    </div>
  );
};


// ============== ANNOUNCEMENTS ==============
const ParentAnnouncements = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['parent-portal-announcements', tenantId],
    queryFn: () => portalApi('/announcements'),
  });

  const markRead = useMutation({
    mutationFn: (id) => portalApi(`/announcements/${id}/read`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['parent-portal-announcements'] }),
  });

  const announcements = data?.announcements || [];
  const unreadCount = data?.unread_count || 0;

  return (
    <div className="space-y-4" data-testid="parent-announcements">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Announcements</h2>
        {unreadCount > 0 && <Badge className="bg-red-500 text-white">{unreadCount} new</Badge>}
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : announcements.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No announcements</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {announcements.map(a => (
            <Card key={a.id} className={!a.is_read ? 'border-amber-200 bg-amber-50/30 cursor-pointer' : ''}
              onClick={() => !a.is_read && markRead.mutate(a.id)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  {!a.is_read && <div className="w-2 h-2 rounded-full bg-amber-500" />}
                  <h3 className="font-semibold text-sm">{a.title}</h3>
                  {a.priority === 'urgent' && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                  {a.priority === 'high' && <Badge className="bg-orange-100 text-orange-700 text-xs">High</Badge>}
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.body}</p>
                <p className="text-xs text-slate-400 mt-2">By {a.author_name} | {new Date(a.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== RISK ALERTS ==============
const ParentRiskAlerts = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['parent-risk-alerts', tenantId],
    queryFn: () => portalApi('/parent/risk-alerts'),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const alerts = data?.alerts || [];

  return (
    <div className="space-y-4" data-testid="parent-risk-alerts">
      <h2 className="text-lg font-semibold">Risk Alerts</h2>
      {alerts.length === 0 ? (
        <Card className="border-dashed border-green-200 bg-green-50/30">
          <CardContent className="py-8 text-center text-green-600 font-medium">
            <CheckCircle className="h-6 w-6 mx-auto mb-2" />
            No risk alerts for your children
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((a, i) => (
            <Card key={a.student_id || `alert-${i}`} className="border-red-200 bg-red-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <h3 className="font-semibold">{a.student_name}</h3>
                </div>
                <div className="flex gap-4 text-sm mb-2">
                  <span>Attendance: <strong>{a.attendance_rate}%</strong></span>
                  {a.avg_grade != null && <span>Avg Grade: <strong>{a.avg_grade}%</strong></span>}
                </div>
                <div className="space-y-1">
                  {a.risk_factors.map((f, j) => (
                    <p key={j} className="text-sm text-red-700"><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />{f}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ============== PARENT FEE STATEMENT ==============
const ParentFeeStatement = ({ tenantId }) => {
  const { flat, termId, setTermId } = useStudentSemesters(portalApi, '/parent/semesters');
  const [allTerms, setAllTerms] = useState(false);
  const { data: statements = [], isLoading } = useQuery({
    queryKey: ['parent-fee-statement', termId, allTerms],
    queryFn: () => portalApi(
      `/fees/statement${!allTerms && termId ? `?term_id=${termId}` : ''}`),
  });

  const picker = (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      {!allTerms && (
        <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                        testId="parent-fees-semester-picker" />
      )}
      <label className="flex items-center gap-1.5 text-xs text-slate-600">
        <input type="checkbox" checked={allTerms} data-testid="parent-fees-all-semesters-toggle"
               onChange={(e) => setAllTerms(e.target.checked)} />
        All semesters
      </label>
    </div>
  );

  const fmt = (n) => formatNumber(n);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;

  if (statements.length === 0) {
    return (
      <div className="space-y-3" data-testid="parent-fee-statement">
        {picker}
        <Card><CardContent className="py-12 text-center text-slate-400">
          No fee records in this semester.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-fee-statement">
      {picker}
      {statements.map(st => (
        <div key={st.student_id} className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">{st.student_name}</h3>
              <p className="text-sm text-slate-500">{st.admission_number}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Balance</p>
              <p className={`text-2xl font-bold ${st.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(st.balance)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Card className="bg-slate-50"><CardContent className="pt-3 pb-2 text-center"><p className="text-xs text-slate-500">Total Charges</p><p className="font-bold">{fmt(st.total_charges)}</p></CardContent></Card>
            <Card className="bg-emerald-50"><CardContent className="pt-3 pb-2 text-center"><p className="text-xs text-slate-500">Total Paid</p><p className="font-bold text-emerald-700">{fmt(st.total_payments)}</p></CardContent></Card>
            <Card className={st.balance > 0 ? 'bg-red-50' : 'bg-emerald-50'}><CardContent className="pt-3 pb-2 text-center"><p className="text-xs text-slate-500">Balance</p><p className={`font-bold ${st.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(st.balance)}</p></CardContent></Card>
          </div>
          {st.charges.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Charges</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {st.charges.map((c, i) => (
                    <div key={c.id || `${c.fee_type_name}-${i}`} className="flex items-center justify-between py-1 border-b last:border-0 text-sm">
                      <span>{c.fee_type_name}{c.description ? ` — ${c.description}` : ''}</span>
                      <span className={c.amount < 0 ? 'text-emerald-600 font-medium' : 'font-medium'}>{fmt(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {st.payments.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Payment History</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {st.payments.map((p, i) => (
                    <div key={p.receipt_number || p.id || `payment-${i}`} className="flex items-center justify-between py-1 border-b last:border-0 text-sm">
                      <div>
                        <span className="font-mono text-xs text-slate-500">{p.receipt_number}</span>
                        <span className="ml-2 capitalize text-slate-600">{(p.payment_method || '').replace(/_/g, ' ')}</span>
                      </div>
                      <span className="font-semibold text-emerald-700">{fmt(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ))}
    </div>
  );
};


// ============== NOTIFICATION PREFERENCES ==============
const ParentNotifPrefs = () => {
  const queryClient = useQueryClient();
  const { data: pref, isLoading } = useQuery({
    queryKey: ['parent-notif-pref'],
    queryFn: () => portalApi('/notification-preference'),
  });

  const saveMutation = useMutation({
    mutationFn: (data) => portalApi('/notification-preference', { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Preference saved'); queryClient.invalidateQueries({ queryKey: ['parent-notif-pref'] }); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const current = pref?.preferred_channel || 'both';
  const options = [
    { value: 'both', label: 'SMS & WhatsApp', desc: 'Receive notifications on both channels', icon: Bell },
    { value: 'sms', label: 'SMS Only', desc: 'Text messages only', icon: Phone },
    { value: 'whatsapp', label: 'WhatsApp Only', desc: 'WhatsApp messages only', icon: Smartphone },
    { value: 'none', label: 'None', desc: 'Do not send me notifications', icon: AlertTriangle },
  ];

  return (
    <div className="space-y-4 max-w-lg" data-testid="parent-notif-prefs">
      <h2 className="text-lg font-bold">Notification Preferences</h2>
      <p className="text-sm text-slate-500">Choose how you'd like to receive school notifications.</p>
      <div className="space-y-2">
        {options.map(opt => (
          <div key={opt.value}
               onClick={() => saveMutation.mutate({ preferred_channel: opt.value })}
               className={`border rounded-lg p-4 cursor-pointer transition-all flex items-center gap-3 ${current === opt.value ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200' : 'hover:border-slate-300'}`}
               data-testid={`notif-pref-${opt.value}`}>
            <opt.icon className={`h-5 w-5 ${current === opt.value ? 'text-indigo-600' : 'text-slate-400'}`} />
            <div className="flex-1">
              <p className="font-medium text-sm">{opt.label}</p>
              <p className="text-xs text-slate-400">{opt.desc}</p>
            </div>
            {current === opt.value && <Badge className="bg-indigo-100 text-indigo-700 border-0">Active</Badge>}
          </div>
        ))}
      </div>
    </div>
  );
};


// ============== MAIN PARENT PORTAL ==============
const ParentPortal = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [collapsed, setCollapsed] = useState(false);

  const { data: tenant } = useQuery({
    queryKey: ['school-tenant-public', tenantId],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/public`); return res.ok ? res.json() : null; },
  });

  const brandColor = tenant?.primary_color || '#f59e0b';

  // Fee lockout status
  const { data: lockoutStatus } = useQuery({
    queryKey: ['parent-fee-lockout-status'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/portal/fee-lockout-status`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      return res.ok ? res.json() : { locked: false, locked_features: [], message: '' };
    },
  });

  const isFeatureLocked = (featureId) => {
    if (!lockoutStatus?.locked) return false;
    return (lockoutStatus.locked_features || []).includes(featureId);
  };

  const LockedOverlay = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-4" data-testid="feature-locked-notice">
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
      </div>
      <h2 className="text-lg font-bold text-slate-800">Access Restricted</h2>
      <p className="text-slate-500 max-w-md text-sm">{lockoutStatus?.message || 'Access restricted due to outstanding fees.'}</p>
    </div>
  );

  const renderContent = () => {
    const featureMap = { timetable: 'timetable', grades: 'grades', report: 'report_card' };
    const mapped = featureMap[activeTab];
    if (mapped && isFeatureLocked(mapped)) return <LockedOverlay />;

    switch (activeTab) {
      case 'dashboard': return <ParentDashboard tenantId={tenantId} />;
      case 'children': return <ParentChildren tenantId={tenantId} />;
      case 'attendance': return <ParentAttendance tenantId={tenantId} />;
      case 'grades': return <ParentGrades tenantId={tenantId} />;
      case 'timetable': return <ParentTimetable tenantId={tenantId} />;
      case 'report': return <ParentReportCard tenantId={tenantId} />;
      case 'announcements': return <ParentAnnouncements tenantId={tenantId} />;
      case 'fees': return <ParentFeeStatement tenantId={tenantId} />;
      case 'risk-alerts': return <ParentRiskAlerts tenantId={tenantId} />;
      case 'notif-prefs': return <ParentNotifPrefs />;
      default: return <ParentDashboard tenantId={tenantId} />;
    }
  };

  // Global fee gate — see StudentPortal.jsx for behaviour.
  if (lockoutStatus?.is_global_lock) {
    return (
      <FeeGateScreen
        status={lockoutStatus}
        onLogout={() => { logout(); navigate(`/school/${tenantId}/login`); }}
      />
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-slate-50" data-testid="parent-portal">
        <div className={cn("flex flex-col h-full bg-slate-900 text-white transition-all duration-300 shrink-0", collapsed ? "w-[68px]" : "w-full max-w-[220px]")}>
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between">
              {!collapsed && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                    <GraduationCap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">{tenant?.name}</p>
                    <p className="text-[10px] text-slate-400">{t('nav.parent_portal')}</p>
                  </div>
                </div>
              )}
              <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-white hover:bg-slate-800">
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1 py-2">
            <div className="px-2 space-y-0.5">
              {MENU.map(item => (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button onClick={() => setActiveTab(item.id)}
                      className={cn("w-full flex items-center gap-3 rounded-lg text-sm transition-colors",
                        collapsed ? "justify-center p-2.5" : "px-3 py-2",
                        activeTab === item.id ? "bg-amber-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800")}
                      data-testid={`parent-nav-${item.id}`}>
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span>{t(item.navKey)}</span>}
                    </button>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">{t(item.navKey)}</TooltipContent>}
                </Tooltip>
              ))}
            </div>
          </ScrollArea>

          <div className="p-3 border-t border-slate-800 space-y-2">
            {!collapsed && <LanguageSwitcher compact />}
            <Button variant="ghost" size={collapsed ? "icon" : "sm"} onClick={() => { logout(); navigate(`/school/${tenantId}/login`); }}
              className={cn("text-slate-400 hover:text-white hover:bg-slate-800", !collapsed && "w-full justify-start gap-2")}
              data-testid="parent-logout-btn">
              <LogOut className="h-4 w-4" />{!collapsed && <span>{t('common.logout')}</span>}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {sessionStorage.getItem('school_preview_token') && (
            <div className="bg-amber-500 text-white px-4 py-2 text-sm text-center font-medium flex items-center justify-center gap-2" data-testid="preview-banner">
              <AlertTriangle className="h-4 w-4" /> Admin Preview Mode — Viewing as Parent
              <button onClick={() => { sessionStorage.removeItem('school_preview_token'); window.close(); }} className="ml-4 underline hover:no-underline">Exit Preview</button>
            </div>
          )}
          <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b px-6 py-3 flex justify-end gap-2 items-center">
            <TranslatePageButton />
            <PortalNotificationBell onClick={() => setActiveTab('announcements')} className="text-slate-600" />
          </div>
          <div className="p-6 max-w-5xl mx-auto">{renderContent()}</div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ParentPortal;
